// Copyright (c) 2025 Annadaneshwar Yadawad. All rights reserved.

import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { closeEventSource } from '../services/notificationService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export const NotificationContext = createContext();

// Toast icon and style mapping
const TOAST_TYPE_MAP = {
    info: {
        style: { background: '#e6f7ff', color: '#0050b3' },
        icon: 'ℹ️',
    },
    warning: {
        style: { background: '#fffbe6', color: '#ad6800' },
        icon: '⚠️',
    },
    important: {
        style: { background: '#fff1f0', color: '#a8071a' },
        icon: '❗',
    },
    welcome: {
        style: { background: '#f6ffed', color: '#237804' },
        icon: '👋',
    },
};

function getToastOptions(type) {
    return TOAST_TYPE_MAP[type] || TOAST_TYPE_MAP.info;
}

export function NotificationProvider({ children }) {
    // Notification state
    const [notifications, setNotifications] = useState(() => {
        const saved = localStorage.getItem('notifications');
        return saved ? JSON.parse(saved) : [];
    });
    const [loading, setLoading] = useState(true);
    const [connectionError, setConnectionError] = useState(null);
    const eventSourceRef = useRef(null);

    // Persist notifications
    useEffect(() => {
        localStorage.setItem('notifications', JSON.stringify(notifications));
    }, [notifications]);

    // Add notification and show toast
    const updateNotifications = useCallback((newNotification) => {
        let message = newNotification.message;
        let type = typeof newNotification.type === 'string' ? newNotification.type : 'info';
        const opts = getToastOptions(type);
        // Add icon to notification object
        const notificationWithIcon = { ...newNotification, message, type, icon: opts.icon, read: false };
        setNotifications(prev => [notificationWithIcon, ...prev]);
        toast(<span><span style={{marginRight: '0.5em'}}>{opts.icon}</span>{message}</span>, {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            icon: false, // We render icon manually in content
            style: opts.style,
        });
    }, []);

    // SSE connection with reconnect logic
    useEffect(() => {
        let reconnectTimer = null;
        let reconnectAttempts = 0;
        const maxDelay = 30000;

        function handleSSEMessage(data) {
            try {
                let newNotification;
                try {
                    newNotification = JSON.parse(data);
                } catch (err) {
                    newNotification = {
                        message: data,
                        id: Date.now().toString(),
                        timestamp: new Date().toISOString(),
                        type: 'info',
                    };
                }
                if (!newNotification.id) newNotification.id = Date.now().toString();
                if (!newNotification.timestamp) newNotification.timestamp = new Date().toISOString();
                updateNotifications(newNotification);
            } catch (error) {
                console.error('Error processing notification:', error);
            }
        }

        function connectSSE() {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
            setLoading(true);
            setConnectionError(null);
            const source = new window.EventSource('https://annadaneshwar-notification-service.azurewebsites.net/public/api/notifications/subscribe');
            eventSourceRef.current = source;

            source.onopen = () => {
                setLoading(false);
                setConnectionError(null);
                reconnectAttempts = 0;
            };
            source.onmessage = (event) => {
                handleSSEMessage(event.data);
            };
            source.addEventListener('initial', (event) => {
                // Optionally show a toast or ignore
            });
            source.addEventListener('notification', (event) => {
                handleSSEMessage(event.data);
            });
            source.onerror = (error) => {
                setConnectionError('Notification connection lost. Reconnecting...');
                setLoading(false);
                source.close();
                eventSourceRef.current = null;
                reconnectAttempts++;
                const delay = Math.min(1000 * 2 ** reconnectAttempts, maxDelay);
                reconnectTimer = setTimeout(connectSSE, delay);
            };
        }
        connectSSE();
        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
            if (reconnectTimer) {
                clearTimeout(reconnectTimer);
            }
            closeEventSource();
        };
    }, [updateNotifications]);

    // Disconnect SSE (for logout)
    const disconnectNotifications = useCallback(() => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        closeEventSource();
    }, []);

    // Mark notification as read
    const markNotificationAsRead = (id) => {
        setNotifications(prev => prev.map(notif =>
            notif.id === id ? { ...notif, read: true } : notif
        ));
    };

    // Clear all notifications
    const clearNotifications = () => {
        setNotifications([]);
    };

    // Manual trigger (for demo)
    const triggerNotification = () => {
        const newNotification = {
            id: Date.now().toString(),
            message: 'Welcome to Servantica! Explore our AI-powered features.',
            timestamp: new Date().toISOString(),
            type: 'welcome',
            read: false
        };
        setNotifications(prev => [newNotification, ...prev]);
    };

    const hasNewNotification = notifications.some(n => !n.read);

    const value = useMemo(() => ({
        notifications,
        setNotifications,
        markNotificationAsRead,
        clearNotifications,
        triggerNotification,
        hasNewNotification,
        disconnectNotifications,
        loading,
        connectionError
    }), [notifications, hasNewNotification, disconnectNotifications, loading, connectionError]);

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
}

NotificationProvider.propTypes = {
    children: PropTypes.node.isRequired
};

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};
